import React, { useState, useEffect, useMemo } from 'react';
import { Users, Download, Printer, FileText, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import reportService from '../../services/report/reportService';
import api from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { SearchableSelect } from '../Common/SearchableSelect';
import type { ClientReportData, ClientReportClient } from '../../types/report';

const ClientReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [reportData, setReportData] = useState<ClientReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [printingClient, setPrintingClient] = useState<number | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      // Fetch all clients so the picker's SearchableSelect (which filters in-memory)
      // can find any client by name. Without an override, the endpoint's default
      // page size (~15) hides every client outside the first page from the search.
      const response = await api.getClients({ per_page: 10000 });
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportService.generateClientReport({
        start_date: startDate,
        end_date: endDate,
        client_id: clientId
      });
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      await reportService.downloadClientPdf({
        start_date: startDate,
        end_date: endDate,
        client_id: clientId
      });
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await reportService.downloadClientExcel({
        start_date: startDate,
        end_date: endDate,
        client_id: clientId
      });
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handlePrintClientPdf = async (clientReportId: number) => {
    try {
      setPrintingClient(clientReportId);
      await reportService.printClientPdf({
        start_date: startDate,
        end_date: endDate,
        client_id: clientReportId
      });
    } catch (err: any) {
      setError(err.message || 'Failed to print client PDF');
    } finally {
      setPrintingClient(null);
    }
  };

  const toggleClientExpansion = (clientIdToToggle: number) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientIdToToggle)) {
        newSet.delete(clientIdToToggle);
      } else {
        newSet.add(clientIdToToggle);
      }
      return newSet;
    });
  };

  // Transform clients to options format for SearchableSelect
  const clientOptions = useMemo(() => {
    return clients.map(client => ({
      id: client.id,
      label: client.client_name || 'Unnamed Client'
    }));
  }, [clients]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Users className="h-7 w-7 mr-3 text-primary-600" />
          Client Commission Report
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Generate commission report for clients by date range
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="client" className="block text-sm font-medium text-gray-700 mb-2">
              Client (Optional)
            </label>
            <SearchableSelect
              options={clientOptions}
              value={clientId}
              onChange={setClientId}
              placeholder="clients"
              allLabel="All Clients"
            />
          </div>

          <div className="flex items-end gap-3">
            <button
              onClick={handleGenerateReport}
              disabled={loading || !startDate || !endDate}
              className="flex-1 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate
            </button>

            {reportData && (
              <>
                <button
                  onClick={handleDownloadPdf}
                  disabled={loading}
                  className="flex-1 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </button>
                <button
                  onClick={handleDownloadExcel}
                  disabled={downloadingExcel}
                  className="flex-1 bg-emerald-700 text-white px-6 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingExcel ? 'Downloading...' : 'Excel'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Generating report...</p>
          </div>
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Period</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(reportData.date_range.start_date)} to {formatDate(reportData.date_range.end_date)}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Gross Commission</p>
                <p className="text-lg font-semibold text-green-700">
                  {formatCurrency(reportData.summary.gross_commission)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Salesman Commission</p>
                <p className="text-lg font-semibold text-blue-700">
                  {formatCurrency(reportData.summary.salesman_commission ?? reportData.summary.user_commission ?? 0)}
                </p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">House Commission</p>
                <p className="text-lg font-semibold text-amber-700">
                  {formatCurrency(reportData.summary.house_commission ?? reportData.summary.net_commission ?? 0)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Clients</p>
                <p className="text-lg font-semibold text-gray-900">
                  {reportData.summary.client_count}
                </p>
              </div>
            </div>
          </div>

          {/* Unassigned Accounts Warning */}
          {reportData.unassigned_accounts && reportData.unassigned_accounts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-800">
                    {reportData.unassigned_accounts.length} Account{reportData.unassigned_accounts.length > 1 ? 's' : ''} Not Assigned to a Client
                  </h3>
                  <p className="mt-1 text-xs text-amber-700">
                    The following accounts have transactions in this date range but are not assigned to any client.
                    Assign them to a client on the Accounts page so they appear in client reports.
                  </p>
                  <div className="mt-3 space-y-1">
                    {reportData.unassigned_accounts.map((account: any) => (
                      <div key={account.account_id} className="flex items-center justify-between text-sm bg-white/60 rounded px-3 py-1.5">
                        <span className="font-medium text-amber-900">{account.account_name}</span>
                        <span className="text-amber-700 text-xs">
                          {account.transaction_count} transaction{account.transaction_count !== 1 ? 's' : ''} &middot; {formatCurrency(account.total_commission)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Clients */}
          {reportData.clients.map((client) => (
            <ClientCard
              key={client.client_id}
              client={client}
              expanded={expandedClients.has(client.client_id)}
              onToggle={() => toggleClientExpansion(client.client_id)}
              onPrint={() => handlePrintClientPdf(client.client_id)}
              isPrinting={printingClient === client.client_id}
              formatCurrency={formatCurrency}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ClientCardProps {
  client: ClientReportClient;
  expanded: boolean;
  onToggle: () => void;
  onPrint: () => void;
  isPrinting: boolean;
  formatCurrency: (amount: number) => string;
}

const ClientCard: React.FC<ClientCardProps> = ({ client, expanded, onToggle, onPrint, isPrinting, formatCurrency }) => {
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(new Set());

  const toggleAccountExpansion = (accountId: number) => {
    setExpandedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Client Header */}
      <div className="px-6 py-4 flex items-center justify-between bg-gray-50">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center text-left hover:bg-gray-100 transition-colors -m-4 p-4 rounded-lg"
        >
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              {client.client_name}
              {expanded ? (
                <ChevronUp className="h-5 w-5 ml-2 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 ml-2 text-gray-400" />
              )}
            </h3>
            <div className="mt-2 flex items-center space-x-6">
              <div>
                <span className="text-xs text-gray-500">Transactions: </span>
                <span className="text-sm font-medium text-gray-900">{client.transaction_count}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500">Gross Commission: </span>
                <span className="text-sm font-semibold text-blue-700">{formatCurrency(client.gross_commission)}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500">Salesman Commission: </span>
                <span className="text-sm font-semibold text-green-700">{formatCurrency(client.salesman_commission ?? client.user_commission ?? 0)}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500">House Commission: </span>
                <span className="text-sm font-semibold text-amber-700">{formatCurrency(client.house_commission ?? client.net_commission ?? 0)}</span>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); onPrint(); }}
          disabled={isPrinting}
          className="ml-4 flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Printer className="h-4 w-4 mr-2" />
          {isPrinting ? 'Printing...' : 'Print PDF'}
        </button>
      </div>

      {/* Accounts */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="space-y-3">
            {client.accounts.map((account) => {
              const isAccountExpanded = expandedAccounts.has(account.account_id);
              return (
                <div key={account.account_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleAccountExpansion(account.account_id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-900">{account.account_name}</p>
                      <div className="mt-1 flex gap-4 text-xs">
                        <span className="text-gray-600">{account.transaction_count} transactions</span>
                        <span className="text-green-700">Gross: {formatCurrency(account.gross_commission)}</span>
                        <span className="text-blue-700">Salesman: {formatCurrency(account.salesman_commission ?? account.user_commission ?? 0)}</span>
                        <span className="text-amber-700 font-medium">House: {formatCurrency(account.house_commission ?? account.net_commission ?? 0)}</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      {isAccountExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {isAccountExpanded && (
                    <div className="border-t border-gray-200 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Bought</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Sold</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Security</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Price</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Account</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Rate</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Gross Comm.</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Comm. %</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">User Comm.</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Employees</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {account.transactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{formatDate(transaction.date)}</td>
                              <td className="px-3 py-2 text-right text-gray-900">{transaction.bought > 0 ? transaction.bought.toLocaleString() : ''}</td>
                              <td className="px-3 py-2 text-right text-gray-900">{transaction.sold > 0 ? transaction.sold.toLocaleString() : ''}</td>
                              <td className="px-3 py-2 text-gray-900">{transaction.security}</td>
                              <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(transaction.price)}</td>
                              <td className="px-3 py-2 text-gray-900">{transaction.account_name || account.account_name}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{(transaction.rate ?? 0).toFixed(4)}</td>
                              <td className="px-3 py-2 text-right font-medium text-blue-700">{formatCurrency(transaction.gross_commission || 0)}</td>
                              <td className="px-3 py-2 text-right text-gray-600">
                                {transaction.employees?.map(emp => `${(emp.percentage ?? 0).toFixed(2)}%`).join(', ')}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-green-700">{formatCurrency(transaction.salesman_commission ?? transaction.user_commission ?? 0)}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {transaction.employees?.map(emp => emp.user_name).join(', ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReport;
